import CustomHeader from "component/CustomHeader";
import NewDashboardLayout from "component/layout/NewDashboardLayout";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Table from "component/table";
import { LoanHistoryTableHeader } from "constants/global";
import { Button } from "component/ui/Button";
import { Drawer } from "component/ui/Drawer";
import { useQueryState } from "nuqs";
import { IoChevronDown } from "react-icons/io5";
import GenerateStatementModal from "component/wallet/GenerateStatementModal";
import useUserDetails from "hooks/useUserDetails";
import { DocumentDownload } from "iconsax-reactjs";
import { useQuery } from "react-query";
import { fetchAllFinance } from "api/credit-fetchers";
import FinanceDrawer from "component/finance/FinanceDrawer";
import { formatDate } from "component/flex/utils";
import FinanceBadge from "component/finance/FinanceBadge";
import InvoiceEmptyState from "component/flex/invoice/InvoiceEmptyState";
import { Bars } from "react-loader-spinner";
import Pagination from "component/pagination";
import FilterMenu from "component/wallet/FilterMenu";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "component/ui/Dropdown";
import {
    displayCurrencyAndAmount,
    FinancePayStatus,
    financeTitles,
} from "constants/FinanceConstants";
import PinModal from "component/pay/PinModal";
import SuccessModal from "component/SuccessModal";
import successIcon from "asset/icons/SuccessCurrencyConversion.svg";

const History = () => {
    const { businessId, currency, walletBalance: currentBalance, user } = useUserDetails();
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [showPinModal, setShowPinModal] = useState(false);
    const [onPinSuccess, setOnPinSuccess] = useState(null);
    const [financeDetails, setFinanceDetails] = useState(null);
    const [open, setOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [showFilter, setShowFilter] = useState(false);
    const [activeFilter, setActiveFilter] = useState(null);
    const [pinError, setPinError] = useState("");
    const [, setGeneratedData] = useState(null);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const navigate = useNavigate();
    const [filters, setFilters] = useState({
        status: [],
        type: [],
        date: { from: null, to: null },
    });
    const [activeSubTab, setActiveSubTab] = useQueryState("subTab", {
        defaultValue: "All",
    });

    const clearFilter = () => {
        setFilters({
            status: [],
            type: [],
            date: { from: null, to: null },
        });
    };

    const { data: allFinancePayData, isLoading: isLoadingAllFinancePayData } = useQuery({
        queryKey: ["fetchAllFinancePay", activeSubTab, currentPage, filters],
        queryFn: () => {
            const payload = {
                businessId,
                status: filters?.status?.length ? filters.status : activeSubTab.toLowerCase(),
                page: currentPage,
                limit: 10,
                currency,
            };
            if (activeSubTab === "all" || activeSubTab === "All") {
                delete payload.status;
            }
            if (filters.date?.from) {
                payload["startDate"] = filters.date?.from;
                payload["endDate"] = filters.date?.to;
            }
            if (filters?.status?.length) {
                payload["status"] = filters.status?.join(", ");
            }
            return fetchAllFinance(payload);
        },
    });

    const allFinancePayList = allFinancePayData?.data;
    const totalPages = allFinancePayData?.meta?.totalPages;
    const dateCount = [Boolean(filters.date?.from), Boolean(filters.date?.to)].filter(
        Boolean
    ).length;
    const totalBadge = filters.status.length + filters.type.length + dateCount;

    return (
        <NewDashboardLayout>
            <Drawer direction="right" open={open} onOpenChange={setOpen}>
                <DropdownMenu>
                    <CustomHeader title="Credit History">
                        <div>
                            <Link
                                to={"/finance/credit/request"}
                                className={`hover:opacity-90 bg-[#6F00FF] mt-0 sm:!mt-0 flex items-center gap-2 text-white rounded-lg py-[10px] px-3 md:!px-5 text-sm`}
                            >
                                Request loan
                            </Link>
                        </div>
                    </CustomHeader>

                    <>
                        <div className="min-h-[70vh] relative rounded-xl pb-1 bg-white w-full">
                            <div className="flex flex-col items-center justify-between gap-2 px-4 pb-4 md:flex-row">
                                <div className="flex w-full gap-2 py-4 overflow-x-auto rounded-lg md:w-3/6 md:overflow-hidden">
                                    {FinancePayStatus.map(({ text, value: subTab }) => (
                                        <button
                                            key={subTab}
                                            className={`rounded py-1 sm:py-2 px-3 text-sm font-medium transition-all whitespace-nowrap duration-300 ${
                                                subTab === activeSubTab
                                                    ? "text-[#6F00FF] bg-[#EBE4FF]"
                                                    : "text-main-grey"
                                            }`}
                                            onClick={() => setActiveSubTab(subTab)}
                                        >
                                            {text}
                                        </button>
                                    ))}
                                </div>

                                <div className="flex flex-wrap items-center justify-center gap-2 p-2 md:gap-x-5">
                                    <button
                                        onClick={() => setShowGenerateModal(true)}
                                        className="flex items-center gap-2 px-4 py-2 text-sm font-[450] rounded-md text-gray-10 bg-secondary-80"
                                    >
                                        <DocumentDownload size={20} />{" "}
                                        <span>Generate statement</span>
                                    </button>
                                    <DropdownMenuTrigger>
                                        <button
                                            className="relative flex items-center gap-2 px-4 py-2 text-sm font-[450] bg-secondary-80 rounded-md text-gray-10"
                                            onClick={() => setShowFilter((v) => !v)}
                                        >
                                            {totalBadge > 0 && (
                                                <span className="bg-[#6F00FF] text-white rounded-full text-xs w-5 h-5 flex items-center justify-center">
                                                    {totalBadge}
                                                </span>
                                            )}
                                            Filter{" "}
                                            <IoChevronDown size={16} className="text-[#6F6B7B]" />
                                        </button>
                                    </DropdownMenuTrigger>
                                    <Button onClick={clearFilter} variant="ghost" className="p-0">
                                        Clear filters
                                    </Button>
                                    {showFilter && (
                                        <DropdownMenuContent className="border-0 shadow-none">
                                            <FilterMenu
                                                show={showFilter}
                                                onClose={() => setShowFilter(false)}
                                                activeFilter={activeFilter}
                                                setActiveFilter={setActiveFilter}
                                                filters={filters}
                                                setFilters={setFilters}
                                                statusList={FinancePayStatus?.map(
                                                    (item) => item.value
                                                )}
                                                filterList={["date", "status"]}
                                            />
                                        </DropdownMenuContent>
                                    )}
                                </div>
                            </div>
                            {isLoadingAllFinancePayData ? (
                                <Bars
                                    wrapperClass="w-max mx-auto mt-[100px] mb-[160px]"
                                    height={30}
                                    width={30}
                                    color="#6F00FF"
                                />
                            ) : allFinancePayList?.length ? (
                                <>
                                    <Table
                                        titles={LoanHistoryTableHeader}
                                            rows={allFinancePayList?.map((payment, index) => {
                                            return {
                                                columns: [
                                                    {
                                                        value: (
                                                            <div className="flex flex-col items-center justify-center h-full">
                                                                <p className="text-sm font-semibold">
                                                                    {formatDate(
                                                                        payment?.createdAt,
                                                                        "MMM D, YYYY"
                                                                    )}
                                                                </p>
                                                            </div>
                                                        ),
                                                        className: "text-gray-70",
                                                    },
                                                    {
                                                        value: (
                                                            <div className="flex items-center h-full text-sm font-semibold">
                                                                {displayCurrencyAndAmount(
                                                                    payment?.currency,
                                                                    payment?.requestedAmount ||
                                                                    payment?.loanAmount ||
                                                                        payment?.inventoryFinance
                                                                            ?.requestedAmount ||
                                                                        payment?.payableFinance
                                                                            ?.requestedAmount ||
                                                                        payment?.receivableFinance
                                                                            ?.requestedAmount
                                                                )}
                                                            </div>
                                                        ),
                                                        className: "text-gray-70",
                                                    },
                                                    {
                                                        value: (
                                                            <div className="flex items-center h-full text-sm font-semibold gap-x-2">
                                                                <span>
                                                                    {
                                                                        financeTitles[
                                                                            payment?.financeType
                                                                        ]
                                                                    }
                                                                </span>
                                                            </div>
                                                        ),
                                                        className: "text-gray-70",
                                                    },
                                                    {
                                                        value: (
                                                            <div className="flex items-center justify-between w-full pr-3">
                                                                <FinanceBadge
                                                                    badgeType={payment?.status}
                                                                />
                                                                <Button
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    className="text-sm text-gray-90"
                                                                    onClick={() => {
                                                                        console.log({ payment });
                                                                        if (
                                                                            payment?.status ===
                                                                            "repaid"
                                                                        ) {
                                                                            navigate(
                                                                                "/finance/credit/" +
                                                                                    payment?._id
                                                                            );
                                                                        } else {
                                                                            setFinanceDetails(
                                                                                payment
                                                                            );
                                                                            setOpen(true);
                                                                        }
                                                                    }}
                                                                >
                                                                    View
                                                                </Button>
                                                            </div>
                                                        ),
                                                        className: "text-main-grey",
                                                    },
                                                ],
                                                rowId: index + payment?._id,
                                            };
                                        })}
                                        wrapperClassName="shadow-none"
                                    />
                                    <div className="flex items-center justify-center w-full mx-auto bottom-1">
                                        <Pagination
                                            currentPage={currentPage}
                                            setCurrentPage={setCurrentPage}
                                            totalPages={totalPages}
                                        />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <InvoiceEmptyState
                                        activeTab={activeSubTab}
                                        link="/finance/credit/request"
                                        linkText="Request loan"
                                        forFinance={true}
                                    />
                                </>
                            )}
                        </div>
                    </>
                    <FinanceDrawer
                        isHistory={true}
                        financeDetails={financeDetails}
                        setOpen={setOpen}
                    />

                    <GenerateStatementModal
                        show={showGenerateModal}
                        onClose={() => setShowGenerateModal(false)}
                        wallet={currentBalance?.accountNumber}
                        email={user?.email || ""}
                        setShowSuccessModal={() => {}}
                        setGeneratedData={setGeneratedData}
                        isForFinance={true}
                        setShowPinModal={setShowPinModal}
                        setOnPinSuccess={setOnPinSuccess}
                    />
                    <PinModal
                        isOpen={showPinModal}
                        onClose={() => {
                            setShowPinModal(false);
                            setPinError("");
                        }}
                        onConfirm={async (enteredPin) => {
                            setPinError("");
                            if (onPinSuccess) {
                                try {
                                    await onPinSuccess(enteredPin);
                                } catch (err) {
                                    console.log({ err });
                                    setPinError("Incorrect PIN. Please try again.");
                                    throw err;
                                }
                            }
                        }}
                        header="Enter your PIN to view statement"
                        pinErrorMessage={pinError}
                    />

                    {showSuccessModal && (
                        <SuccessModal
                            show={true}
                            onClose={() => setShowSuccessModal(false)}
                            icon={
                                <div className="p-1 rounded-full">
                                    <img src={successIcon} alt="success" className="w-24 h-24" />
                                </div>
                            }
                            title="Your statement is ready!"
                            message="We’ve sent a copy to your email."
                            buttonText="Done"
                            loading={false}
                            onButtonClick={() => {
                                setShowSuccessModal(false);
                            }}
                        />
                    )}
                </DropdownMenu>
            </Drawer>
        </NewDashboardLayout>
    );
};

export default History;
